import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { KpiCard } from '@/components/KpiCard';
import { StatusBadge } from '@/components/StatusBadge';
import { api, downloadFile } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatINR, formatNumber, formatDateTime, PLATFORM_ACCENTS } from '@/lib/format';
import { platformLogo } from '@/lib/logos';
import { ArrowRight, Coins, IndianRupee, ScrollText, Sparkles, Download, MapPin, Search, Wallet, Send } from 'lucide-react';
import { toast } from 'sonner';
import UserStockWallet from '@/pages/user/StockWallet';
import { DashboardBanners } from '@/components/DashboardBanners';
import UserStockRecharge from '@/pages/user/StockRecharge';
import KnownDevices from '@/pages/user/KnownDevices';
import { TrustedBadge } from '@/components/TrustedBadge';
import { LimitUsageBar } from '@/components/LimitUsageBar';

const INVOICEABLE = ['paid', 'completed', 'pending', 'paid_bridge_failed'];

export default function UserDashboardPage() {
    const { user, setUser } = useAuth();
    const [payments, setPayments] = useState([]);
    const [searchParams] = useSearchParams();
    const _qt = searchParams.get('tab');
    const initialTab = ['history', 'stock', 'stock-recharge', 'security'].includes(_qt) ? _qt : 'overview';
    const [tab, setTab] = useState(initialTab);
    useEffect(() => { if (['history', 'stock', 'stock-recharge', 'security', 'overview'].includes(_qt || 'overview')) setTab(_qt || 'overview'); }, [_qt]);
    const [billing, setBilling] = useState({ city: '', state: '', pincode: '' });
    const [savingBilling, setSavingBilling] = useState(false);
    const [historyQuery, setHistoryQuery] = useState('');

    useEffect(() => {
        if (user) setBilling({ city: user.city || '', state: user.state || '', pincode: user.pincode || '' });
    }, [user]);

    const billingComplete = Boolean((user?.city || '').trim() && (user?.state || '').trim());

    const saveBilling = async () => {
        if (!billing.city.trim() || !billing.state.trim()) { toast.error('City and State are required'); return; }
        setSavingBilling(true);
        try {
            const { data } = await api.patch('/auth/profile', billing);
            setUser(data.user);
            toast.success('Billing details saved');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save billing details');
        } finally { setSavingBilling(false); }
    };

    const load = async () => {
        const p = await api.get('/recharge/my');
        setPayments(p.data.payments || []);
    };
    useEffect(() => { load(); }, []);

    const completed = payments.filter(p => ['completed', 'paid'].includes(p.status));
    const totalSpent = completed.reduce((s, p) => s + (p.amount_inr || 0), 0);
    const pending = payments.filter(p => p.status === 'pending').length;

    const getInvoice = async (paymentId) => {
        try {
            await downloadFile(`/recharge/invoice/${paymentId}`, `invoice-${paymentId}.pdf`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not download invoice');
        }
    };

    return (
        <AppShell>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold" data-testid="dashboard-welcome">Welcome back, {user?.name || 'there'}</h1>
                    <p className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">Your recharge activity.{user?.stock_wallet_unlocked && <TrustedBadge testid="dashboard-trusted-badge" />}</p>
                </div>
                <div className="flex gap-2">
                    <Button asChild className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="dashboard-recharge-now-button">
                        <Link to="/recharge"><Sparkles className="h-4 w-4 mr-1" /> Recharge Now</Link>
                    </Button>
                </div>
            </div>

            <DashboardBanners />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
                <KpiCard testid="dashboard-kpi-recharges" label="Total recharges" value={formatNumber(completed.length)} icon={Sparkles} accent={{ bg: '#E9FBF8', color: '#1F9D8A' }} />
                <KpiCard testid="dashboard-kpi-spent" label="Total spent" value={formatINR(totalSpent)} icon={IndianRupee} accent={{ bg: '#EFF6FF', color: '#2563EB' }} />
                <KpiCard testid="dashboard-kpi-pending" label="Pending" value={formatNumber(pending)} icon={ScrollText} accent={{ bg: '#FFF7ED', color: '#B45309' }} />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="overview" data-testid="dashboard-tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="history" data-testid="dashboard-tab-history">Recharge History</TabsTrigger>
                    {user?.stock_wallet_unlocked && <TabsTrigger value="stock" data-testid="dashboard-tab-stock">Stock Wallet</TabsTrigger>}
                    {user?.stock_wallet_unlocked && <TabsTrigger value="stock-recharge" data-testid="dashboard-tab-stock-recharge">Stock Recharge</TabsTrigger>}
                    <TabsTrigger value="security" data-testid="dashboard-tab-security">Security</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-3"><LimitUsageBar /></div>
                    <Card className="p-5 lg:col-span-2">
                        <div className="font-display font-semibold">Recent recharges</div>
                        <div className="mt-3 overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow><TableHead>Date</TableHead><TableHead>Platform</TableHead><TableHead>In-app ID</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Status</TableHead></TableRow>
                                </TableHeader>
                                <TableBody>
                                    {payments.slice(0, 5).map((p) => (
                                        <TableRow key={p.id}>
                                            <TableCell className="whitespace-nowrap">{formatDateTime(p.created_at)}</TableCell>
                                            <TableCell><span className="capitalize">{p.platform}</span></TableCell>
                                            <TableCell className="font-mono text-xs">{p.in_app_id}</TableCell>
                                            <TableCell className="text-right">{formatINR(p.amount_inr)}<div className="text-[10px] text-muted-foreground">{formatNumber(p.diamonds_or_coins)} {p.unit}</div></TableCell>
                                            <TableCell className="text-right"><StatusBadge status={p.status} /></TableCell>
                                        </TableRow>
                                    ))}
                                    {payments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No recharges yet. <Link className="text-[#1F9D8A] underline" to="/recharge">Start your first recharge</Link>.</TableCell></TableRow>}
                                </TableBody>
                            </Table>
                        </div>
                    </Card>
                    <Card className="p-5">
                        <div className="font-display font-semibold">Quick links</div>
                        <div className="mt-3 grid grid-cols-1 gap-2">
                            {['olaparty', 'trumeet'].map((k) => {
                                const a = PLATFORM_ACCENTS[k];
                                return (
                                    <Link key={k} to={`/recharge?platform=${k}`} className="flex items-center justify-between p-3 rounded-xl border border-border hover:shadow-card transition-shadow">
                                        <div className="flex items-center gap-2">
                                            {platformLogo(k) ? (
                                                <img src={platformLogo(k)} alt={k} className="h-8 w-8 rounded-lg object-cover" />
                                            ) : (
                                                <div className="h-8 w-8 rounded-lg flex items-center justify-center text-sm font-display font-semibold" style={{ background: a.bg, color: a.color }}>{k.charAt(0).toUpperCase()}</div>
                                            )}
                                            <div className="capitalize text-sm font-medium">Recharge {k}</div>
                                        </div>
                                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                    </Link>
                                );
                            })}
                        </div>
                    </Card>
                    <Card className="p-5 lg:col-span-3" data-testid="dashboard-billing-card">
                        <div className="flex items-center gap-2 font-display font-semibold"><MapPin className="h-4 w-4 text-[#1F9D8A]" /> Billing address</div>
                        <p className="text-xs text-muted-foreground mt-1">Your name, email and WhatsApp come from your account and can't be edited here. Add your city &amp; state to unlock recharges. Pincode is optional.</p>
                        {!billingComplete && (
                            <div className="mt-3 text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2" data-testid="billing-incomplete-warning">
                                Recharges are locked until you add your billing city and state below.
                            </div>
                        )}
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Name</Label>
                                <Input value={user?.name || ''} readOnly disabled className="bg-secondary/60" data-testid="billing-name-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Email</Label>
                                <Input value={user?.email || ''} readOnly disabled className="bg-secondary/60" data-testid="billing-email-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">WhatsApp</Label>
                                <Input value={user?.mobile || user?.whatsapp || ''} readOnly disabled className="bg-secondary/60" data-testid="billing-whatsapp-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">City <span className="text-rose-500">*</span></Label>
                                <Input value={billing.city} onChange={e => setBilling(b => ({ ...b, city: e.target.value }))} placeholder="City" data-testid="billing-city-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">State <span className="text-rose-500">*</span></Label>
                                <Input value={billing.state} onChange={e => setBilling(b => ({ ...b, state: e.target.value }))} placeholder="State" data-testid="billing-state-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Pincode <span className="text-muted-foreground">(optional)</span></Label>
                                <Input value={billing.pincode} onChange={e => setBilling(b => ({ ...b, pincode: e.target.value }))} placeholder="Pincode" data-testid="billing-pincode-input" />
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <Button onClick={saveBilling} disabled={savingBilling} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="billing-save-button">
                                {savingBilling ? 'Saving…' : 'Save billing address'}
                            </Button>
                        </div>
                    </Card>
                </TabsContent>
                <TabsContent value="history" className="mt-4">
                    <Card className="p-4 sm:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                            <div className="font-display font-semibold">All recharges</div>
                            <div className="relative w-full sm:w-72">
                                <Input
                                    value={historyQuery}
                                    onChange={e => setHistoryQuery(e.target.value)}
                                    placeholder="Search by in-app ID…"
                                    className="pl-8"
                                    data-testid="user-history-search-input"
                                />
                                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            </div>
                        </div>
                        <div className="overflow-x-auto" data-testid="user-recharge-history-table">
                            <Table>
                                <TableHeader>
                                    <TableRow><TableHead>Date</TableHead><TableHead>Platform</TableHead><TableHead>In-app ID</TableHead><TableHead>Plan</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Status</TableHead><TableHead className="text-right">Invoice</TableHead></TableRow>
                                </TableHeader>
                                <TableBody>
                                    {payments.filter(p => !historyQuery.trim() || String(p.in_app_id || '').toLowerCase().includes(historyQuery.trim().toLowerCase())).map((p) => (
                                        <TableRow key={p.id}>
                                            <TableCell className="whitespace-nowrap">{formatDateTime(p.created_at)}</TableCell>
                                            <TableCell><span className="capitalize">{p.platform}</span></TableCell>
                                            <TableCell className="font-mono text-xs">{p.in_app_id}</TableCell>
                                            <TableCell>{formatNumber(p.diamonds_or_coins)} {p.unit}</TableCell>
                                            <TableCell className="text-right">{formatINR(p.amount_inr)}</TableCell>
                                            <TableCell className="text-right"><StatusBadge status={p.status} /></TableCell>
                                            <TableCell className="text-right">
                                                {INVOICEABLE.includes(p.status) ? (
                                                    <Button size="sm" variant="outline" onClick={() => getInvoice(p.id)} data-testid={`user-invoice-download-${p.id}`}>
                                                        <Download className="h-3.5 w-3.5 mr-1" /> Invoice
                                                    </Button>
                                                ) : <span className="text-xs text-muted-foreground">—</span>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {payments.filter(p => !historyQuery.trim() || String(p.in_app_id || '').toLowerCase().includes(historyQuery.trim().toLowerCase())).length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{historyQuery.trim() ? 'No recharges match that in-app ID.' : 'No recharges yet.'}</TableCell></TableRow>}
                                </TableBody>
                            </Table>
                        </div>
                    </Card>
                </TabsContent>
                {user?.stock_wallet_unlocked && <TabsContent value="stock" className="mt-4">
                    <UserStockWallet />
                </TabsContent>}
                {user?.stock_wallet_unlocked && <TabsContent value="stock-recharge" className="mt-4">
                    <UserStockRecharge />
                </TabsContent>}
                <TabsContent value="security" className="mt-4">
                    <KnownDevices />
                </TabsContent>
            </Tabs>
        </AppShell>
    );
}